import React from "react";
import PropTypes from "prop-types";
import Relay from "react-relay";
import { Link } from "react-router";
import ChannelIcon from "../channel-defs/ChannelIcon.react";
import PagingGrid, { defaultPageSize, processPagingParams } from "../fw/PagingGrid.react";
import AppPanel from "../fw/AppPanel.react";

import types from "../../common/model-api/types";
import urlMapping from "../app/URLMapping";
import { injectIntl, defineMessages, FormattedMessage, FormattedDate, FormattedTime } from "react-intl";
import { wrapNotFound } from "../app/NotFoundHandler.react";
import RefreshButton from "../jobs/RefreshButton.react";
import { ERROR_PERMANENT, ERROR_TEMPORARY, ERROR_OK, ERROR_UNKNOWN } from "../../common/model-api/ChannelErrorDefType";
import ConfirmDialog from "../fw/ConfirmDialog.react";
import MarkChannelErrorMutation from "./MarkChannelErrorMutation.react";
import LoadingButton from "../fw/LoadingButtonNew.react";
import * as BrowserPermission from "../BrowserPermission";
import * as Permission from "../../common/util/Permission";
import { renderErrorTitle } from "./ChannelErrorList.react";
import ChannelErrorBox from "./ChannelErrorBox.react";
import SafeHTML from "../fw/SafeHTML.react";
import moment from "moment";
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: { id: "channelErrorsDef.title", defaultMessage: "List of Errors" },
    displayNameTitle: { id: "channelErrors.name", defaultMessage: "Display name" },
    searchName: { id: "channelErrorsDef.searchName", defaultMessage: "Search by code" },
    referencedId: { id: "channelErrorsDef.referencedId", defaultMessage: "Search by referenced document" },
    createdAtAfter: { id: "fetchList.createdAtAfter", defaultMessage: "Created after" },
    createdAtBefore: { id: "fetchList.createdAtBefore", defaultMessage: "Created before" },
    allFlows: { id: "channelErrorsDef.allFlows", defaultMessage: "All flows" },
    allOrgs: { id: "channelErrorsDef.allOrgs", defaultMessage: "All organisations" },
});

interface ChannelErrorListUserProps {
 intl: IntlShape;
 relay?: object;
 user: User;
}

class ChannelErrorListUser extends React.Component<ChannelErrorListUserProps> {
    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    typeName = "channelErrors";

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            loadingError: false,
            filterOrganisationId: null,
        };
    }

    invokeMarkOk() {
        let { user } = this.props;
        let flow = user.organisation.flow;

        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                resolve();
            };

            this.props.relay.commitUpdate(
                new MarkChannelErrorMutation({
                    flow: flow,
                    actionMarkOk: true,
                }),
                { onSuccess: onSuccess, onFailure: reject }
            );
        });
    }

    onClickMarkOk() {
        return new Promise((resolve, reject) => {
            let confirmDialogConfig = {
                message: this.props.intl.formatMessage(messages.markOkDescription),
                confirm: this.props.intl.formatMessage(messages.markOkButton),
                confirmStyle: "btn-danger",
            };

            return ConfirmDialog(this.props.intl, confirmDialogConfig)
                .then(() => {
                    return this.invokeMarkOk().then(() => {
                        return resolve();
                    });
                })
                .catch(() => {
                    resolve();
                });
        });
    }

    search = (event) => {
        this.setState({
            loading: true,
            loadingError: false,
        });

        window.history.pushState({}, '', window.location.pathname);

        let values = {};

        if (this.refs?.searchDisplayNameBoxRef?.value) {
            values["displayNameLike"] = this.refs.searchDisplayNameBoxRef.value;
        }
        if (this.refs?.searchReferenceIdBoxRef?.value) {
            values["referencedId"] = this.refs.searchReferenceIdBoxRef.va.lue;
        }
        if (this.refs?.flowSelectRef?.value) {
            values["flowId"] = this.refs.flowSelectRef.value;
        }
        if (this.refs?.orgSelectRef?.value) {
            values["organisationId"] = this.refs.orgSelectRef.value;
        }

        if (this.refs?.createdAtAfterBoxRef?.value) {
            const afterDate = this.refs.createdAtAfterBoxRef.value;
            const afterTime = this.refs.createdAtAfterTimeBoxRef.value || '00:00';
            values["lastErrorTransferAtAfter"] = `${afterDate}T${afterTime}:00`;
        }

        if (this.refs?.createdAtBeforeBoxRef?.value) {
            const beforeDate = this.refs.createdAtBeforeBoxRef.value;
            const beforeTime = this.refs.createdAtBeforeTimeBoxRef.value || '23:59';
            values["lastErrorTransferAtBefore"] = `${beforeDate}T${beforeTime}:59`;
        }

        // Add URL parameter handling
        const searchParams = new URLSearchParams(window.location.search);
        Object.entries(values).forEach(([key, value]) => {
            if (value) {
                searchParams.set(key, value);
            } else {
                searchParams.delete(key);
            }
        });

        // Update URL without reload
        window.history.pushState({}, '', `${window.location.pathname}?${searchParams.toString()}`);

        this.props.relay.setVariables(values, (state) => {
            if (state.done) {
                this.setState({
                        loading: false,
                        loadingError: false,
                    });
            } else if (state.error) {
                this.setState({
                        loading: false,
                        loadingError: true,
                    });
            }
        });
    };

    reset = () => {
        if (this.refs?.searchDisplayNameBoxRef) {
            this.refs.searchDisplayNameBoxRef.value = "";
        }
        if (this.refs?.searchReferenceIdBoxRef) {
            this.refs.searchReferenceIdBoxRef.value = "";
        }
        if (this.refs?.flowSelectRef) {
            this.refs.flowSelectRef.value = "";
        }
        if (this.refs?.orgSelectRef) {
            this.refs.orgSelectRef.value = "";
        }
        if (this.refs?.createdAtAfterBoxRef) {
            this.refs.createdAtAfterBoxRef.value = "";
        }
        if (this.refs?.createdAtAfterTimeBoxRef) {
            this.refs.createdAtAfterTimeBoxRef.value = "";
        }

        // Clear URL parameters
        window.history.pushState({}, '', window.location.pathname);

        this.props.relay.setVariables({
            displayNameLike: null,
            referenceIdLike: null,
            referenceId: null,
            flowId: null,
            organisationId: null,
            lastErrorTransferAtAfter: null,
            lastErrorTransferAtBefore: null,
        });

        this.forceUpdate();
    };

    renderInvoices(data, organisationId) {
        let toFindRow = {
            organisation_id: organisationId,
        };
        return (
            <p style={{ color: "#888" }}>
                Týká se {data.displaynames.length} dokladů{" "}
                {data.displaynames.reduce((acc, item, idx, arr) => {
                    // let finalUrl = urlMapping.getUrl('channelErrors', 'view', {...toFindRow, id: data.ids[idx]})
                    acc.push(
                        <Link href={data.urls[idx]} key={idx}>
                            {item}
                        </Link>
                    );
                    if (idx < arr.length - 1) {
                        acc.push(" | ");
                    }
                    return acc;
                }, [])}
            </p>
        );
    }


    handleOrgClick = (e) => {
        console.log("Org clicked:", e.target.value);
        this.setState({
            loading: true,
            loadingError: false,
            filterOrganisationId: e.target.value,
        });
    };

    render() {
        const { user } = this.props;
        let data = user;
        let list = JSON.parse(user.channelErrorTypes);

        const idToNameOrganisation = list.idToNameOrganisation;
        const lastErrorTransferAtAfter = list.lastErrorTransferAtAfter;
        list = list.fullResult;
        list.sort((a, b) => {
            const dateCompare = new Date(b.lasterrortransferat) - new Date(a.lasterrortransferat);
            if (dateCompare === 0) {
                return b.displaynames.length - a.displaynames.length;
            }
            return dateCompare;
        });
        let type = types[this.typeName];

        let tableListUrlPart = "list-user";

        return (
            <AppPanel breadcrumbParams={[{ type, data: data }]} className="channelErrors" title={this.props.intl.formatMessage(messages.title)}>
                <h3>
                    <FormattedMessage defaultMessage="Errors" id="channelErrors.list" />
                </h3>

                <div className="btn-toolbar" role="toolbar">
                    <RefreshButton relay={this.props.relay} />

                    <div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
                        <Link activeClassName="active" className="btn btn-default" to={urlMapping.getUrl(type, tableListUrlPart + "-type", data)}><FormattedMessage defaultMessage="Grouped by channel error" id="channelErrors.groupedView" /></Link>
                        <Link className="btn btn-default" to={urlMapping.getUrl(type, tableListUrlPart + "-fail", data)}><FormattedMessage defaultMessage="Tabular view" id="channelErrors.tabularView" /></Link>
                    </div>

                    <div className="input-group" style={{ maxWidth: "200px" }}>
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.searchName)} ref="searchDisplayNameBoxRef" type="text" />
                    </div>

                    <div className="input-group" style={{ maxWidth: "200px", paddingRight: "1em" }}>
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.referencedId)} ref="searchReferenceIdBoxRef" type="text" />
                    </div>

                    <div className="input-group">
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtAfter)} ref="createdAtAfterBoxRef" style={{ width: "11em" }}  type="date" value={lastErrorTransferAtAfter ? lastErrorTransferAtAfter.substring(0, 10) : ""} />
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtAfter)} ref="createdAtAfterTimeBoxRef" style={{ width: "5em" }} type="time" value={lastErrorTransferAtAfter ? lastErrorTransferAtAfter.substring(11, 16) : ""} />
                    </div>
                    <div className="input-group" style={{ paddingRight: "1em" }}>
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtBefore)} ref="createdAtBeforeBoxRef" style={{ width: "11em" }} type="date" />
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtBefore)} ref="createdAtBeforeTimeBoxRef" style={{ width: "5em" }} type="time" />
                    </div>

                    {(
                        <div className="input-group" style={{ maxWidth: "200px" }}>
                            <select className="form-control" name="search" onChange={this.handleOrgClick} ref="orgSelectRef">
                                <option value="">{this.props.intl.formatMessage(messages.allOrgs)}</option>
                                {idToNameOrganisation.map((data, idx) => (
                                    <option key={data.id + idx} value={data.id}>{data.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {(
                        <div className="input-group" style={{ maxWidth: "200px", paddingRight: "1em" }}>
                            {(() => {
                                const options = list.filter((data) => this.state.filterOrganisationId ? data.flow.organisation_id === this.state.filterOrganisationId : true);

                                if (options.length === 0) {
                                    return null;
                                }

                                return (
                                    <select className="form-control" name="search" onChange={this.handleFlowClick} ref="flowSelectRef">
                                        <option value="">{this.props.intl.formatMessage(messages.allFlows)}</option>
                                        {options.map((data, idx) => (
                                            <option key={data.flow.id + idx} value={data.flow.id}>
                                                {data.flow.flowName}
                                            </option>
                                        ))}
                                    </select>
                                );
                            })()}
                        </div>
                    )}

                    <div aria-label="..." className="btn-group" role="group" style={{ paddingRight: "1em" }}>
                        <div className="input-group-btn">
                            <button className="btn btn-outline-secondary" onClick={this.search} type="button">
                                {this.state.loading ? <span className="fa fa-refresh fa-spin" /> : this.state.loadingError ? <span className="fa fa-exclamation" style={{ color: "red" }} /> : <span className="fa fa-search" />}
                            </button>
                        </div>
                        <div className="input-group-btn">
                            <button className="btn btn-outline-secondary" onClick={this.reset} type="button">
                                <span className="fa fa-times" />
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ margin: "1.5em 0" }}>
                </div>

                {list.map((data, idx) => (
                    <ChannelErrorBox accordion additionalData={{
                        organisationId: data.flow.organisation_id,
                        organisationName: idToNameOrganisation.find((org) => org.id === data.flow.organisation_id)?.name || "none",
                        flow: data.flow,
                        lastErrorTransferAt: moment(data.lasterrortransferat).format("DD.MM.YYYY HH:mm"),
                        locale: this.props.intl.locale,
                        count: data.displaynames.length,
                    }} channelDef={data.channelDef} channelErrorDef={data.data} channelErrorMessages={[data.channelErrorMessage]} key={data.flow_id + "|" + data.channelErrorDef_id + "|" + idx}>
                        <div style={{ margin: "1.5em 0" }}>{this.renderInvoices(data)}</div>
                    </ChannelErrorBox>
                ))}
            </AppPanel>
        );
    }
}

export default Relay.createContainer(
    wrapNotFound(injectIntl(ChannelErrorListUser), (props) => {
        return props.user;
    }),
    {
        initialVariables: {
            first: defaultPageSize,
            last: null,
            after: null,
            before: null,
            type: null,
            success: null,
            resolvableBy: null,
            displayNameLike: null,
            referencedIdLike: null,
            referencedId: null,
            hasError: null,
            organisationId: null,
            flowId: null,
            instanceId: null,
            lastErrorTransferAtAfter: null,
            lastErrorTransferAtBefore: null,
        },
        prepareVariables(variables) {
            processPagingParams(variables);

            if (variables.success === "fail") {
                variables.hasError = true;
            } else if (variables.success === "ok") {
                variables.hasError = false;
            } else {
                variables.hasError = null;
            }

            if (variables.displayNameLike) {
                variables.hasError = null;
            }

            if (variables.referencedIdLike) {
                variables.hasError = null;
            }
            if (variables.referencedId) {
                variables.hasError = null;
            }
            if (variables.organisationId) {
                variables.hasError = null;
            }
            if (variables.flowId) {
                variables.hasError = null;
            }

            const searchParams = new URLSearchParams(window.location.search);

            if (searchParams.has('displayNameLike')) {
                variables.displayNameLike = searchParams.get('displayNameLike');
            }
            if (searchParams.has('referencedId')) {
                variables.referencedId = searchParams.get('referencedId');
            }

            if (searchParams.has('flowId')) {
                variables.flowId = searchParams.get('flowId');
            }
            if (searchParams.has('organisationId')) {
                variables.organisationId = searchParams.get('organisationId');
            }

            if (searchParams.has('lastErrorTransferAtAfter')) {
                variables.lastErrorTransferAtAfter = searchParams.get('lastErrorTransferAtAfter');
            }
            if (searchParams.has('lastErrorTransferAtBefore')) {
                variables.lastErrorTransferAtBefore = searchParams.get('lastErrorTransferAtBefore');
            }

            return variables;
        },
        fragments: {
            user: () => Relay.QL`
            fragment on User {
                channelErrorTypes(type: $type, hasError: $hasError, displayNameLike: $displayNameLike, resolvableBy: $resolvableBy, organisationId: $organisationId, flowId: $flowId, lastErrorTransferAtAfter: $lastErrorTransferAtAfter, lastErrorTransferAtBefore: $lastErrorTransferAtBefore),
            }
        `,
        },
    }
);
